import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from "@react-oauth/google";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import GetStarted from './pages/GetStarted/GetStarted';
import Login from './pages/Auth/Login';
import LoginOtp from './pages/Auth/LoginOtp';
import PersonalInformation from './pages/Auth/PersonalInformation';
import Otp from './pages/Auth/Otp';
import SetPassword from './pages/Auth/SetPassword';
import Success from './pages/Auth/Success';
import EmailVerification from './pages/Auth/EmailVerification';
import Home from './pages/Dashboard/Home';
import PasswordRecovery from './pages/Auth/PasswordRecovery';
import PasswordResetOtp from './pages/Auth/PasswordResetOtp';
import BusinessDirectorKyc from './pages/Auth/BusinessDirectorKyc';
import { Helmet } from 'react-helmet-async';
import PrivateRoute from './hooks/PrivateRoute';
import AuthRoute from './hooks/AuthRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import LoadingScreen from './components/Loading/Loading';
import { useEffect } from 'react';
import { fetchAllowedCountries, fetchCurrencyOptions, fetchExchangeRates, updateExchangeRates } from './redux/slices/countriesSlice';
import useAutoLogout from './hooks/useAutoLogout';
import useVersionCheck from './hooks/useVersionCheck';
import { logout } from './actions/auth';
import { generateExchangeRates } from './hooks/generateExchangeRates';
import CatchAllRedirect from './hooks/CatchAllRedirect';
import NotFound from './pages/NotFound/NotFound';
import KycSuccess from './components/Success/Success';
import InstallPWA from "./components/InstallPWA/InstallPWA";
import { TrackingScripts } from './components/TrackingScripts';
import { isProduction } from './utils/env';

function App() {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const conversionRates = useSelector(state => state.country.conversionRates);
  const currencyOptions = useSelector(state => state.country.currencyOptions);
  const isLoading = useSelector(state => state.country.isLoading);

  useAutoLogout(logout, token, navigate, dispatch);
  useVersionCheck();

  useEffect(() => {
    dispatch(fetchExchangeRates());
    dispatch(fetchCurrencyOptions());
    dispatch(fetchAllowedCountries()); 
  }, [dispatch]);

  useEffect(() => {
    if (conversionRates.length > 0 && currencyOptions.length > 0) {
      const updatedRates = generateExchangeRates(conversionRates, currencyOptions);
      dispatch(updateExchangeRates(updatedRates));
    }
  }, [conversionRates, currencyOptions, dispatch]);

  const maintenance = process.env.REACT_APP_ENVIRONMENT == 'maintenance';
  if (process.env.REACT_APP_ENVIRONMENT === 'production') {
    console.log = () => {};
    console.warn = () => {};
    console.error = () => {};
    
    setInterval(() => {
      if (window.outerWidth - window.innerWidth > 500) {
        console.clear();
      }
    }, 10000);
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <div className="App">
          <TrackingScripts />
          {isLoading && <LoadingScreen />}
          {/* <InstallPWA /> */}
          <Helmet>
            <title>Cardplot App</title>
            <meta name="description" content="At Cardplot, we make your everyday transactions seamless and efficient with our Airtime & Bill Payment feature. Whether you're recharging your phone, paying utility bills, or settling subscriptions, our solution is designed to meet those needs." />
            <meta name="keywords" content="At Cardplot, we make your everyday transactions seamless and efficient with our Airtime & Bill Payment feature. Whether you're recharging your phone, paying utility bills, or settling subscriptions, our solution is designed to meet those needs." />
            <meta charSet="utf-8" />
            <meta name="robots" content={isProduction ? "index, follow" : "noindex, nofollow"} />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
          </Helmet>

          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            closeOnClick={true}
            pauseOnHover={true}
            draggable={true}
          />

          {maintenance ? (
            <Route path="*" element={<CatchAllRedirect />} />
          ) : (
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              
              <Route path='/otp' element={<Otp />} />
              <Route path='/password' element={<SetPassword />} />
              <Route path='/auth/success' element={<Success />} />
              <Route path='/kyc/success' element={<KycSuccess />} />
              <Route path='/login-otp' element={<LoginOtp />} />
              <Route path='/business-director/:kycVerificationReference/complete-kyc' element={<BusinessDirectorKyc />} />

              {/* Private Routes */}
              <Route element={<AuthRoute />}>
                <Route path='/login' element={<Login />} />
                <Route path='/getstarted' element={ <GetStarted />} />
                <Route path='/getstarted/email-verification' element={<EmailVerification />} />
                <Route path='/personal/signup' element={<PersonalInformation />} />
                <Route path='/password-reset' element={<PasswordRecovery />} />
                <Route path='/password-otp' element={<PasswordResetOtp />} />
              </Route>

              <Route element={<PrivateRoute />}>
                <Route path="/user/*" element={<Home />} />
              </Route>
              

              {/* Catch-all redirect */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          )}
      </div>
    </GoogleOAuthProvider>
  );
}

export default App;